/**
 * Sof — ИПДС: источник происхождения денежных средств (Flow step="sof"). Доступна после KYC
 * GREEN. Клиент прикладывает документы (НДФЛ / справка о доходах / прочее, до 10 фото/PDF),
 * добавляет комментарий и отправляет на проверку; обрабатывает роль broker в админ-панели.
 * Заявок можно подавать сколько угодно — экран показывает историю + форму новой заявки.
 * По одобрению брокер увеличивает дополнительный лимит вывода клиента. Пропсы: onBack.
 */
import { useCallback, useEffect, useState, type ChangeEvent } from "react";
import { useBackButton, useMainButton } from "@/telegram/useMainButton";
import { FallbackButton, Screen } from "@/components/Screen";
import { Note } from "@/components/Note";
import { Spinner } from "@/components/Spinner";
import { Badge } from "@/components/Badge";
import {
  friendlyError,
  sofApi,
  type SofApplication,
  type SofFileMeta,
  type SofKind,
} from "@/lib/api";
import { formatUsdt } from "@/lib/format";

const KINDS: { key: SofKind; label: string }[] = [
  { key: "ndfl", label: "НДФЛ" },
  { key: "income_certificate", label: "Справка о доходах" },
  { key: "other", label: "Прочее" },
];
const KIND_LABEL: Record<SofKind, string> = { ndfl: "НДФЛ", income_certificate: "Справка о доходах", other: "Прочее" };
const MAX_FILES = 10;

function appDate(iso: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
}

function HistoryCard({ a }: { a: SofApplication }) {
  const badge =
    a.status === "approved" ? (
      <Badge tone="ok">Одобрено</Badge>
    ) : a.status === "rejected" ? (
      <Badge tone="err">Отклонено</Badge>
    ) : (
      <Badge tone="warn">На проверке</Badge>
    );
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-surface-card px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[13px] text-white/55">{appDate(a.submitted_at)}</span>
        {badge}
      </div>
      {a.status === "approved" && a.approved_amount_usd != null && (
        <div className="mt-1 text-[14px] text-brand">Лимит увеличен на {formatUsdt(a.approved_amount_usd)}</div>
      )}
      {a.status === "rejected" && a.reject_reason && (
        <div className="mt-1 text-[13px] text-white/60">Причина: {a.reject_reason}</div>
      )}
      {a.comment && <div className="mt-1 text-[13px] text-white/45">{a.comment}</div>}
    </div>
  );
}

export function Sof({ onBack }: { onBack: () => void }) {
  const [applications, setApplications] = useState<SofApplication[]>([]);
  const [kycOk, setKycOk] = useState(true);
  const [files, setFiles] = useState<SofFileMeta[]>([]);
  const [comment, setComment] = useState("");
  const [kind, setKind] = useState<SofKind>("ndfl");
  const [busy, setBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [ov, fl] = await Promise.all([sofApi.overview(), sofApi.files()]);
      setApplications(ov.applications);
      setKycOk(ov.kyc_ok);
      setComment(ov.draft.comment ?? "");
      setFiles(fl.files);
    } catch {
      /* ignore */
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || busy) return;
    setBusy(true);
    setError(null);
    try {
      await sofApi.uploadFile(kind, f);
      await reload();
    } catch (err) {
      setError(friendlyError(err, "Не удалось загрузить файл"));
    } finally {
      setBusy(false);
    }
  };

  const removeFile = async (id: string) => {
    setBusy(true);
    try {
      await sofApi.deleteFile(id);
      await reload();
    } catch {
      /* ignore */
    } finally {
      setBusy(false);
    }
  };

  const submit = useCallback(async () => {
    if (files.length === 0 || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await sofApi.submit(comment.trim() || undefined);
      setComment("");
      await reload();
    } catch (err) {
      setError(friendlyError(err, "Не удалось отправить"));
    } finally {
      setSubmitting(false);
    }
  }, [files.length, submitting, comment, reload]);

  useBackButton(onBack);
  useMainButton({
    text: submitting ? "Отправляем..." : "Отправить на проверку",
    onClick: submit,
    visible: kycOk,
    active: files.length > 0,
    loading: submitting,
  });

  if (!loaded) {
    return (
      <Screen title="Источник происхождения денежных средств">
        <div className="mt-10 flex justify-center">
          <Spinner className="h-8 w-8" />
        </div>
      </Screen>
    );
  }

  if (!kycOk) {
    return (
      <Screen title="Источник происхождения денежных средств">
        <Note variant="warning">Сначала пройдите верификацию личности (KYC).</Note>
      </Screen>
    );
  }

  return (
    <Screen title="Источник происхождения денежных средств" subtitle="Приложите документы, подтверждающие доход">
      {applications.length > 0 && (
        <div className="mb-5 space-y-2">
          <div className="text-[13px] font-medium text-white/55">Ваши заявки</div>
          {applications.map((a) => (
            <HistoryCard key={a.id} a={a} />
          ))}
        </div>
      )}

      <div className="text-[13px] font-medium text-white/55">Новая заявка</div>
      <Note className="mb-4 mt-2">Фото или PDF, до {MAX_FILES} документов.</Note>

      {/* выбор типа для следующего файла */}
      <div className="mb-3 flex flex-wrap gap-2">
        {KINDS.map((k) => (
          <button
            key={k.key}
            onClick={() => setKind(k.key)}
            className={`rounded-full border px-3 py-1.5 text-[13px] ${
              kind === k.key ? "border-brand/70 bg-brand/15 text-white" : "border-white/[0.08] text-white/60"
            }`}
          >
            {k.label}
          </button>
        ))}
      </div>

      <label
        className={`flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-white/15 px-4 py-4 text-[15px] ${
          files.length >= MAX_FILES || busy ? "opacity-50" : "active:bg-white/[0.03]"
        }`}
      >
        {busy ? "Загружаем…" : `➕ Добавить документ (${KIND_LABEL[kind]})`}
        <input
          type="file"
          accept="image/*,application/pdf"
          hidden
          disabled={files.length >= MAX_FILES || busy}
          onChange={onFile}
        />
      </label>

      {files.length > 0 && (
        <ul className="mt-3 space-y-2">
          {files.map((f) => (
            <li
              key={f.id}
              className="flex items-center justify-between gap-3 rounded-2xl border border-white/[0.06] bg-surface-card px-4 py-3"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-[14px]">{KIND_LABEL[f.kind]}</span>
                <span className="block text-[12px] text-white/45">
                  {f.mime === "application/pdf" ? "PDF" : "Фото"} · {Math.round(f.size / 1024)} KB
                </span>
              </span>
              <button
                onClick={() => removeFile(f.id)}
                disabled={busy}
                className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-[15px] text-white/40 active:bg-white/10"
                aria-label="Удалить"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Комментарий (необязательно)"
        rows={3}
        className="mt-3 w-full resize-none rounded-2xl border border-white/[0.08] bg-surface-card px-4 py-3 text-[15px] text-white placeholder:text-white/35 focus:border-brand/60 focus:outline-none"
      />

      {error && (
        <Note variant="danger" className="mt-4">
          {error}
        </Note>
      )}

      <FallbackButton
        text={submitting ? "..." : "Отправить на проверку"}
        onClick={submit}
        disabled={files.length === 0 || submitting}
        loading={submitting}
      />
    </Screen>
  );
}
